import { Modal } from "@mantine/core";
import { useLocation, useSearchParams } from "react-router";
import { useContainerRef } from "../../../../hooks/useContainerRef";
import { useShouldFieldBeHighlighted } from "../../../../hooks/useShouldFieldBeHighlighted";
import { ModalFooter } from "../../../../mantine/components/ModalFooter/ModalFooter";
import { useToast } from "../../../../mantine/notifications/useToast";
import type { Delivery } from "../../../../utils/backend-types";
import { LoadOrError } from "../../../LoadOrError/LoadOrError";
import { DeliveryForm } from "./DeliveryForm/DeliveryForm";
import { EditForm } from "./EditForm/EditForm";
import { useDelivery } from "./hooks/useDelivery";
import { useDeliveryFormOptions } from "./hooks/useDeliveryFormOptions";
import { useDeliveryMutations } from "./hooks/useDeliveryMutations";

export interface DeliveryModalProps {
  deliveryId?: string;
  contractId?: string;
  projectId: string;
  onSubmit: (delivery: Delivery) => void;
  onClose: () => void;
}

function DeliveryModal({
  deliveryId,
  projectId,
  onSubmit,
  onClose
}: DeliveryModalProps) {
  const { showToast } = useToast();
  const [searchParams] = useSearchParams();
  const { containerNode, containerRef } = useContainerRef();
  const { delivery, isDeliveryDataLoading, deliveryLoadingError } =
    useDelivery(deliveryId);
  const { deliveryCreateMutation, deliveryUpdateMutation } =
    useDeliveryMutations(projectId);
  const contractId = searchParams.get("contract");

  const {
    data: deliveryFormOptions,
    isLoading: isDeliveryFormOptionsLoading,
    error: deliveryFormOptionsError
  } = useDeliveryFormOptions(projectId);

  const { state: missingData } = useLocation();
  const missingFields =
    (missingData && missingData.missingFields.general) || [];
  const shouldFieldBeHighlighted = useShouldFieldBeHighlighted(missingFields);

  async function handleSubmitCreateDelivery(delivery: Delivery) {
    try {
      const response = await deliveryCreateMutation.mutateAsync(delivery);
      onSubmit(response);
      showToast({
        type: "success",
        message: "Lieferung wurde erfolgreich hinzugefügt"
      });
    } catch (error) {
      return Promise.reject(error);
    }
  }
  const title = deliveryId ? "Übersicht Lieferung" : "Lieferung hinzufügen";

  async function handleSubmitEditDelivery(delivery: Delivery) {
    await deliveryUpdateMutation.mutateAsync({
      ...delivery
    });

    onSubmit(delivery);
  }

  const editMode = typeof deliveryId !== "undefined";

  return (
    <Modal
      className="DeliveryModal"
      opened
      size="xl"
      title={title}
      onClose={onClose}
    >
      <LoadOrError
        error={deliveryFormOptionsError || deliveryLoadingError}
        loading={isDeliveryFormOptionsLoading || isDeliveryDataLoading}
      >
        {deliveryFormOptions &&
          (editMode ? (
            delivery && (
              <EditForm
                buttonContainer={containerNode}
                delivery={delivery}
                missingFields={missingFields}
                options={deliveryFormOptions}
                shouldFieldBeHighlighted={shouldFieldBeHighlighted}
                onCancel={onClose}
                onSubmit={handleSubmitEditDelivery}
              />
            )
          ) : (
            <DeliveryForm
              buttonContainer={containerNode}
              delivery={delivery}
              initialContract={contractId}
              missingFields={[]}
              options={deliveryFormOptions}
              shouldFieldBeHighlighted={shouldFieldBeHighlighted}
              onCancel={onClose}
              onSubmit={handleSubmitCreateDelivery}
            />
          ))}
      </LoadOrError>
      <ModalFooter ref={containerRef} />
    </Modal>
  );
}

export { DeliveryModal };
